import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { platformApi } from '../../api/platformApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Loader2, Plus, Search, RefreshCw, KeyRound, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

export const PlatformUsersGlobal = () => {
    const { token } = useAuth();
    // Data State
    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(null); // "Cargando X de Y..."
    const [searchQuery, setSearchQuery] = useState('');
    const [dataLoaded, setDataLoaded] = useState(false); // Have we loaded users yet?
    const [loadErrors, setLoadErrors] = useState([]); // Store errors per tenant

    // Create Modal State
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        email: '',
        password: '',
        full_name: '',
        role: 'ADMIN_DESPACHO',
        tenant_id: '',
        activo: true
    });

    useEffect(() => {
        // Always load tenants for the filter/select
        loadTenants();
    }, []);

    const loadTenants = async () => {
        try {
            const data = await platformApi.getTenants(token, true); // Active & Inactive
            setTenants(data);
        } catch (e) {
            toast.error("Error al cargar tenants");
        }
    };

    const loadGlobalUsers = async () => {
        if (tenants.length === 0) {
            toast.error("No hay tenants cargados");
            return;
        }

        setLoading(true);
        setUsers([]);
        setLoadErrors([]);

        try {
            const total = tenants.length;
            let completed = 0;
            let allUsers = [];

            // Chunked execution to avoid browser freeze if many tenants (batch size 5)
            const batchSize = 5;
            for (let i = 0; i < total; i += batchSize) {
                const batch = tenants.slice(i, i + batchSize);
                setLoadingProgress(`Cargando usuarios (${completed}/${total} tenants)...`);

                const promises = batch.map(t => platformApi.getTenantUsers(token, t.id));

                const results = await Promise.allSettled(promises);

                results.forEach((res, idx) => {
                    const tenant = batch[idx];
                    if (res.status === 'fulfilled') {
                        // Enrich user objects with tenant info
                        const tenantUsers = res.value.map(u => ({
                            ...u,
                            _tenantName: tenant.nombre || tenant.slug || "Unknown",
                            _tenantId: tenant.id
                        }));
                        allUsers = [...allUsers, ...tenantUsers];
                    } else {
                        console.error(`Error loading users for tenant ${tenant.slug}:`, res.reason);
                        setLoadErrors(prev => [...prev, { tenant: tenant.nombre || tenant.slug, error: "Falló la carga" }]);
                    }
                });

                completed += batch.length;
            }

            setUsers(allUsers);
            setDataLoaded(true);
            setLoadingProgress(null);
        } catch (e) {
            toast.error("Error general al cargar usuarios");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!createForm.email || !createForm.password || !createForm.tenant_id) {
            toast.error("Email, Contraseña y Tenant son obligatorios");
            return;
        }

        try {
            // Adapt form data to API expectation
            const payload = {
                email: createForm.email,
                full_name: createForm.full_name || createForm.email.split('@')[0], // Fallback if name empty
                role: createForm.role
            };

            // Call API
            const res = await platformApi.createTenantUser(token, createForm.tenant_id, payload);

            toast.success("Usuario creado correctamente");

            // Show new user in list (optimistic or reload)
            // Ideally we just reload, but that's expensive. Let's push to list.
            const tenant = tenants.find(t => t.id === createForm.tenant_id);
            const newUser = {
                ...res.user, // Assuming API returns created user
                _tenantName: tenant?.nombre || "Unknown",
                _tenantId: tenant?.id,
                activo: true // Default
            };

            setUsers(prev => [newUser, ...prev]);
            setCreateOpen(false);
            setCreateForm({
                email: '',
                password: '',
                full_name: '',
                role: 'ADMIN_DESPACHO',
                tenant_id: '',
                activo: true
            });

        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.detail || "Error al crear usuario");
        }
    };

    const toggleStatus = async (user) => {
        try {
            await platformApi.updateUserStatus(token, user.id, !user.activo);
            // Update local state
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, activo: !u.activo } : u));
            toast.success("Estado actualizado");
        } catch (e) {
            toast.error("Error al actualizar estado");
        }
    };

    const safeLower = (text) => text ? text.toString().toLowerCase() : '';

    const filtered = users.filter(u =>
        safeLower(u.email).includes(safeLower(searchQuery)) ||
        safeLower(u.full_name || u.nombre).includes(safeLower(searchQuery)) ||
        safeLower(u._tenantName).includes(safeLower(searchQuery))
    );

    const getAvailableRoles = (tenantId) => {
        const tenant = tenants.find(t => t.id === tenantId);
        if (!tenant) return [];
        const type = tenant.tenant_type;

        switch (type) {
            case 'LAW_FIRM':
                return [
                    { value: 'ADMIN_DESPACHO', label: 'Admin Despacho' },
                    { value: 'OPERADOR', label: 'Operador' },
                    { value: 'CLIENTE', label: 'Cliente (Invitado)' }
                ];
            case 'COMPANY':
                return [
                    { value: 'ADMIN_EMPRESA', label: 'Admin Empresa' },
                    { value: 'OPERADOR', label: 'Operador' }
                ];
            case 'END_USER':
                return [
                    { value: 'CLIENTE', label: 'Usuario Final (Cliente)' },
                    { value: 'OPERADOR', label: 'Operador' }
                ];
            case 'INTERNAL_FIRM':
                return [
                    { value: 'ADMIN_DESPACHO', label: 'Admin Interno' },
                    { value: 'OPERADOR', label: 'Operador Interno' }
                ];
            default:
                return [
                    { value: 'OPERADOR', label: 'Operador' },
                    { value: 'CLIENTE', label: 'Cliente' }
                ];
        }
    };

    // Auto-select role when tenant changes
    const handleTenantChange = (tenantId) => {
        const roles = getAvailableRoles(tenantId);
        const defaultRole = roles.length > 0 ? roles[0].value : 'OPERADOR';
        setCreateForm(prev => ({ ...prev, tenant_id: tenantId, role: defaultRole }));
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="relative w-64">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por email, nombre o tenant..."
                        className="pl-8"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="flex gap-2 items-center">
                    {loading && (
                        <div className="flex items-center text-sm text-gray-500 gap-2 mr-2">
                            <Loader2 className="animate-spin h-4 w-4" />
                            {loadingProgress || "Cargando..."}
                        </div>
                    )}

                    {!dataLoaded && !loading && (
                        <Button variant="outline" onClick={loadGlobalUsers}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Cargar Todos los Usuarios
                        </Button>
                    )}

                    {dataLoaded && (
                        <Button variant="ghost" size="icon" onClick={loadGlobalUsers} title="Recargar">
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    )}

                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" /> Nuevo Usuario
                    </Button>
                </div>
            </div>

            {loadErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm mb-4">
                    <p className="font-semibold">Errores de carga:</p>
                    <ul className="list-disc pl-5 mt-1">
                        {loadErrors.slice(0, 5).map((err, idx) => (
                            <li key={idx}>Error al cargar usuarios de <b>{err.tenant}</b></li>
                        ))}
                        {loadErrors.length > 5 && <li>... y {loadErrors.length - 5} más</li>}
                    </ul>
                </div>
            )}

            <div className="border rounded-md bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Usuario</TableHead>
                            <TableHead>Rol</TableHead>
                            <TableHead>Tenant</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!dataLoaded && !loading ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-10 text-gray-500">
                                    <p>Haga clic en "Cargar Todos los Usuarios" para visualizar la lista global.</p>
                                </TableCell>
                            </TableRow>
                        ) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-6 text-gray-500">
                                    {loading ? "Cargando..." : "No se encontraron usuarios"}
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map(u => (
                                <TableRow key={u.id}>
                                    <TableCell>
                                        <div className="font-medium">{u.full_name || u.nombre || '(Sin Nombre)'}</div>
                                        <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </TableCell>
                                    <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                                    <TableCell>
                                        <div className="font-medium text-sm">{u._tenantName}</div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={u.activo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                            {u.activo ? "Activo" : "Inactivo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)}>
                                            {u.activo ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-green-500" />}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create User Modal */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Crear Usuario</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Tenant *</Label>
                            <Select
                                value={createForm.tenant_id}
                                onValueChange={handleTenantChange}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar Tenant" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tenants.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.nombre || t.slug}
                                            {!t.activo && " (Inactivo)"}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {/* Validation Warning */}
                            {createForm.tenant_id && tenants.find(t => t.id === createForm.tenant_id && !t.activo) && (
                                <p className="text-xs text-amber-600">
                                    MANTENIMIENTO: El usuario no podrá acceder porque el tenant está inactivo.
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                placeholder="ejemplo@empresa.com"
                                type="email"
                                value={createForm.email}
                                onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Nombre Completo</Label>
                            <Input
                                placeholder="Juan Pérez"
                                value={createForm.full_name}
                                onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })}
                            />
                        </div>

                        {/* Password field is actually needed for creation via API usually, 
                             unless API auto-generates it. Plan said "Password (required)". */}
                        <div className="space-y-2">
                            <Label>Contraseña *</Label>
                            <Input
                                placeholder="******"
                                type="password"
                                value={createForm.password}
                                onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Rol</Label>
                            <Select
                                value={createForm.role}
                                onValueChange={v => setCreateForm({ ...createForm, role: v })}
                                disabled={!createForm.tenant_id}
                            >
                                <SelectTrigger><SelectValue placeholder={!createForm.tenant_id ? "Selecciona un tenant primero" : "Seleccionar Rol"} /></SelectTrigger>
                                <SelectContent>
                                    {createForm.tenant_id && getAvailableRoles(createForm.tenant_id).map(role => (
                                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreateUser}>Crear Usuario</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
